import React, { useState, useEffect } from 'react';
import {
    Card,
    Table,
    Button,
    Space,
    Tag,
    Typography,
    Input,
    Select,
    message,
    Statistic,
    Row,
    Col,
    Descriptions,
    Modal,
    Tooltip,
    Empty
} from 'antd';
import {
    MessageOutlined,
    SearchOutlined,
    EyeOutlined,
    UserOutlined,
    BarChartOutlined,
    FilterOutlined,
    ReloadOutlined,
    MailOutlined
} from '@ant-design/icons';
import moment from 'moment';
import feedbackService from '../../../services/feedbackService';
import DashboardLayout from '../../../components/Dashboard/DashboardLayout';
import PageHeader from '../../../components/Common/PageHeader';
import { toTitleCase } from '../../../utils/format';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;
const { TextArea } = Input;

const AdminFeedback = () => {
    const [feedbacks, setFeedbacks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statisticsLoading, setStatisticsLoading] = useState(true);
    const [statistics, setStatistics] = useState(null);
    const [filters, setFilters] = useState({
        type: null,
        search: ''
    });
    const [activeFilters, setActiveFilters] = useState({
        type: null,
        search: ''
    });
    const [searchText, setSearchText] = useState('');
    const [typeFilter, setTypeFilter] = useState(null);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });
    const [selectedFeedback, setSelectedFeedback] = useState(null);
    const [detailModalVisible, setDetailModalVisible] = useState(false);

    const loadFeedbacks = async () => {
        try {
            setLoading(true);
            const params = {
                page: pagination.current,
                per_page: pagination.pageSize,
                ...activeFilters
            };
            const response = await feedbackService.adminGetFeedbacks(params);
            if (response.status === 'success') {
                setFeedbacks(response.data);
                if (response.pagination) {
                    setPagination(prev => ({
                        ...prev,
                        total: response.pagination.total
                    }));
                }
            }
        } catch (error) {
            message.error('Failed to load feedbacks');
        } finally {
            setLoading(false);
        }
    };

    const loadStatistics = async () => {
        try {
            setStatisticsLoading(true);
            const response = await feedbackService.adminGetStatistics();
            if (response.status === 'success') {
                setStatistics(response.data);
            }
        } catch (error) {
            console.error('Error loading statistics:', error);
        } finally {
            setStatisticsLoading(false);
        }
    };

    // Load feedbacks when filters or pagination changes
    useEffect(() => {
        loadFeedbacks();
    }, [activeFilters, pagination.current, pagination.pageSize]);

    // Load statistics once on mount
    useEffect(() => {
        loadStatistics();
    }, []);

    // Reset pagination to page 1 when active filters change
    useEffect(() => {
        setPagination(prev => {
            if (prev.current === 1) {
                return prev; // No need to update if already on page 1
            }
            return {
                ...prev,
                current: 1
            };
        });
    }, [activeFilters]);

    const handleSearch = () => {
        setActiveFilters({
            type: typeFilter,
            search: searchText.trim()
        });
        // Pagination will be reset to 1 by the useEffect when activeFilters changes
    };

    const handleClearFilters = () => {
        setSearchText('');
        setTypeFilter(null);
        setActiveFilters({
            type: null,
            search: ''
        });
        // Pagination will be reset to 1 by the useEffect when activeFilters changes
    };

    const hasActiveFilters = activeFilters.search || activeFilters.type;
    const hasPendingChanges = searchText !== activeFilters.search || 
                              typeFilter !== activeFilters.type;

    const handleViewFeedback = (feedback) => {
        setSelectedFeedback(feedback);
        setDetailModalVisible(true);
    };

    const getTypeLabel = (type) => {
        const labels = {
            general: 'General Feedback',
            bug_report: 'Bug Report',
            feature_request: 'Feature Request',
            suggestion: 'Suggestion',
            complaint: 'Complaint',
            compliment: 'Compliment',
            other: 'Other'
        };
        return labels[type] || type;
    };

    const getTypeColor = (type) => {
        const colors = {
            general: 'blue',
            bug_report: 'red',
            feature_request: 'green',
            suggestion: 'orange',
            complaint: 'volcano',
            compliment: 'gold',
            other: 'default'
        };
        return colors[type] || 'default';
    };

    const columns = [
  
        {
            title: 'User',
            key: 'user',
            width: 200,
            render: (_, record) => {
                const userName = record.user 
                    ? `${record.user.first_name || ''} ${record.user.last_name || ''}`.trim() || record.user.email
                    : 'Unknown';
                return (
                    <div>
                        <Text strong>{toTitleCase(userName)}</Text>
                        {record.user?.email && (
                            <div>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    {record.user.email}
                                </Text>
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            width: 150,
            render: (type) => (
                <Tag color={getTypeColor(type)}>
                    {getTypeLabel(type)}
                </Tag>
            )
        },
        {
            title: 'Subject',
            dataIndex: 'subject',
            key: 'subject',
            width: 250,
            ellipsis: true,
        },
        {
            title: 'Message Preview',
            dataIndex: 'message',
            key: 'message',
            width: 300,
            ellipsis: true,
            render: (text) => (
                <Tooltip title={text}>
                    <Text>{text?.substring(0, 100)}{text?.length > 100 ? '...' : ''}</Text>
                </Tooltip>
            )
        },
        {
            title: 'Submitted',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 150,
            render: (date) => moment(date).format('MMM DD, YYYY HH:mm')
        },
        {
            title: 'Actions',
            key: 'actions',
            fixed: 'right',
            width: 80,
            render: (_, record) => (
                <Button
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={() => handleViewFeedback(record)}
                >
                </Button>
            )
        }
    ];

    return (
        <DashboardLayout>
            <PageHeader
                title="Feedback Management"
                description="View and manage all user feedback submissions"
            />

            {/* Statistics Cards */}
            {statistics && (
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic
                                title="Total Feedbacks"
                                value={statistics.total}
                                prefix={<MessageOutlined />}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic
                                title="Last 7 Days"
                                value={statistics.recent_7_days}
                                prefix={<BarChartOutlined />}
                                valueStyle={{ color: '#52c41a' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic
                                title="This Month"
                                value={statistics.this_month}
                                prefix={<BarChartOutlined />}
                                valueStyle={{ color: '#faad14' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Button
                                type="default"
                                icon={<ReloadOutlined />}
                                onClick={() => {
                                    loadFeedbacks();
                                    loadStatistics();
                                }}
                                loading={loading || statisticsLoading}
                            >
                                Refresh
                            </Button>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Search and Filter Bar */}
            <Card 
                style={{ 
                    marginBottom: 20,
                    borderRadius: 12,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    border: '1px solid #f0f0f0'
                }}
            >
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={24} md={10} lg={8}>
                        <Search
                            placeholder="Search by subject, message, or user..."
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onPressEnter={handleSearch}
                            onSearch={handleSearch}
                            allowClear
                            size="large"
                        />
                    </Col>
                    <Col xs={12} sm={12} md={7} lg={6}>
                        <Select
                            placeholder="All Types"
                            value={typeFilter}
                            onChange={setTypeFilter}
                            style={{ width: '100%' }}
                            size="large"
                            allowClear
                        >
                            <Option value="general">General Feedback</Option>
                            <Option value="bug_report">Bug Report</Option>
                            <Option value="feature_request">Feature Request</Option>
                            <Option value="suggestion">Suggestion</Option>
                            <Option value="complaint">Complaint</Option>
                            <Option value="compliment">Compliment</Option>
                            <Option value="other">Other</Option>
                        </Select>
                    </Col>
                    <Col xs={12} sm={12} md={7} lg={4}>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={handleSearch}
                            size="large"
                            block
                        >
                            Search
                        </Button>
                    </Col>
                </Row>
                {hasActiveFilters && (
                    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
                        <Space size="small" wrap>
                            <Text strong>Active Filters:</Text>
                            {activeFilters.search && (
                                <Tag closable onClose={() => {
                                    setSearchText('');
                                    setActiveFilters(prev => ({ ...prev, search: '' }));
                                }} color="blue">
                                    Search: "{activeFilters.search}"
                                </Tag>
                            )}
                            {activeFilters.type && (
                                <Tag closable onClose={() => {
                                    setTypeFilter(null);
                                    setActiveFilters(prev => ({ ...prev, type: null }));
                                }} color="geekblue">
                                    Type: {getTypeLabel(activeFilters.type)}
                                </Tag>
                            )}
                            <Button type="link" size="small" onClick={handleClearFilters}>
                                Clear All
                            </Button>
                        </Space>
                    </div>
                )}
            </Card>

            {/* Feedback Table */}
            <Card>
                <Table
                    scroll={{ x: 1200 }}
                    columns={columns}
                    dataSource={feedbacks}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showTotal: (total) => `Total ${total} feedbacks`,
                        onChange: (page, pageSize) => {
                            setPagination(prev => ({
                                ...prev,
                                current: page,
                                pageSize: pageSize || prev.pageSize
                            }));
                        },
                        onShowSizeChange: (current, size) => {
                            setPagination(prev => ({
                                ...prev,
                                current: 1,
                                pageSize: size
                            }));
                        }
                    }}
                    locale={{
                        emptyText: (
                            <Empty
                                description="No feedbacks found"
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                            />
                        )
                    }}
                />
            </Card>

            {/* Feedback Detail Modal */}
            <Modal
                title={
                    <Space>
                        <MessageOutlined />
                        <span>Feedback Details</span>
                    </Space>
                }
                open={detailModalVisible}
                onCancel={() => {
                    setDetailModalVisible(false);
                    setSelectedFeedback(null);
                }}
                footer={[
                    <Button key="close" onClick={() => {
                        setDetailModalVisible(false);
                        setSelectedFeedback(null);
                    }}>
                        Close
                    </Button>
                ]}
                width={700}
            >
                {selectedFeedback && (
                    <Descriptions column={1} bordered>
                        <Descriptions.Item label="Feedback ID">
                            <Text strong>#{selectedFeedback.id}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="User">
                            <div>
                                <Text strong>
                                    {selectedFeedback.user 
                                        ? `${selectedFeedback.user.first_name || ''} ${selectedFeedback.user.last_name || ''}`.trim() || selectedFeedback.user.email
                                        : 'Unknown'}
                                </Text>
                                {selectedFeedback.user?.email && (
                                    <div>
                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                            <MailOutlined /> {selectedFeedback.user.email}
                                        </Text>
                                    </div>
                                )}
                                {selectedFeedback.user?.phone_number && (
                                    <div>
                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                            📞 {selectedFeedback.user.phone_number}
                                        </Text>
                                    </div>
                                )}
                            </div>
                        </Descriptions.Item>
                        <Descriptions.Item label="Type">
                            <Tag color={getTypeColor(selectedFeedback.type)}>
                                {getTypeLabel(selectedFeedback.type)}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Subject">
                            <Text strong>{selectedFeedback.subject}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Message">
                            <div style={{ 
                                background: '#f5f5f5', 
                                padding: '12px', 
                                borderRadius: '4px',
                                maxHeight: '300px',
                                overflowY: 'auto'
                            }}>
                                <Text>{selectedFeedback.message}</Text>
                            </div>
                        </Descriptions.Item>
                        <Descriptions.Item label="Submitted">
                            {moment(selectedFeedback.created_at).format('MMMM DD, YYYY [at] HH:mm:ss')}
                        </Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>
        </DashboardLayout>
    );
};

export default AdminFeedback;

